import { endOfMonth, format, startOfMonth } from 'date-fns'
import { t } from 'i18next'

import { SubscriptionInvoiceStatus, SubscriptionInvoiceType } from '../../common/enums.js'
import { formatPrice } from '../../common/utils/utils.js'
import { InvoiceDTO } from '../../server/svea/types.js'

export interface SimpleInvoiceStatus {
    text: string
    color: SimpleInvoiceColor
}

export type SimpleInvoiceColor = 'red' | 'orange' | 'green' | 'gray'

export class Invoice {
    readonly data: InvoiceDTO
    readonly dateFormat: string = 'dd.MM.yyyy'

    readonly ID: string
    readonly status: string
    readonly isLegacy: boolean
    readonly currency: string
    readonly amount: number
    readonly pdfUrl: string
    readonly dueDate?: Date
    readonly invoiceDate?: Date
    readonly type: SubscriptionInvoiceType
    readonly fixedPeriod?: string

    constructor(data: InvoiceDTO) {
        this.data = data
        this.ID = this.data.invoiceId
        this.isLegacy = !!this.data.isLegacy
        this.status = this.data.status
        this.status = this.data.status
        this.amount = this.data.amount
        this.currency = this.data.currency
        this.pdfUrl = this.data.pdfUrl
        this.type = this.data.type
        this.dueDate = this.data.dueDate ? new Date(this.data.dueDate) : undefined
        this.invoiceDate = this.data.invoiceDate ? new Date(this.data.invoiceDate) : undefined
        this.fixedPeriod = this.data.period
    }

    get formattedAmount() {
        return formatPrice(this.amount, this.currency)
    }

    get period() {
        if (this.fixedPeriod) {
            return this.fixedPeriod
        }

        if (this.isLegacy && this.invoiceDate) {
            const start = format(startOfMonth(this.invoiceDate), this.dateFormat)
            const end = format(endOfMonth(this.invoiceDate), this.dateFormat)

            return `${start} - ${end}`
        }

        if (this.dueDate) {
            const start = format(startOfMonth(this.dueDate), this.dateFormat)
            const end = format(endOfMonth(this.dueDate), this.dateFormat)

            return `${start} - ${end}`
        }

        return '-'
    }

    get formattedDueDate() {
        if (this.dueDate) {
            return format(this.dueDate, this.dateFormat)
        }

        return '-'
    }

    get deviceName(): string {
        return this.data.description
    }

    get deviceNameTruncated(): string {
        const maxLength = 30
        if (this.deviceName.length <= maxLength) {
            return this.deviceName
        }
        return this.deviceName.substring(0, maxLength) + '...'
    }

    get deviceLines(): string {
        if (!this.data.lines || this.data.lines.length === 0) {
            return 'No items'
        }

        // Format each line with description and total
        return this.data.lines
            .map((line) => {
                const total = formatPrice(line.totalWithVat, this.currency)
                return `${line.description}: ${total}`
            })
            .join('\n')
    }

    get simplifiedStatus(): SimpleInvoiceStatus {
        if (this.isLegacy) {
            return this.legacySimplifiedStatus
        }

        if (this.type === SubscriptionInvoiceType.CREDIT) {
            // TODO: how should we handle this?
            return {
                text: t('portal.invoice.status.paid'),
                color: 'green',
            }
        } else {
            switch (this.status) {
                case SubscriptionInvoiceStatus.PENDING:
                case SubscriptionInvoiceStatus.ACTIVE:
                    return {
                        text: t('portal.invoice.status.outstanding'),
                        color: 'orange',
                    }

                case SubscriptionInvoiceStatus.PAID:
                    return {
                        text: t('portal.invoice.status.paid'),
                        color: 'green',
                    }

                case SubscriptionInvoiceStatus.REFUNDED:
                case SubscriptionInvoiceStatus.CANCELLED:
                    return {
                        text: t('portal.invoice.status.terminated'),
                        color: 'red',
                    }

                default:
                    return {
                        text: t('portal.invoice.status.unknown'),
                        color: 'gray',
                    }
            }
        }
    }

    private get legacySimplifiedStatus(): SimpleInvoiceStatus {
        const outstandingStatuses = ['UNPAID', 'LOSS']

        const paidStatuses = ['PAID', 'PAID_WITH_A_LOSS', 'PAID_WITH_A_CREDITED', 'PAID_WITH_A_TERMINATED']

        const partiallyPaidStatuses = [
            'PARTIALLY_PAID',
            'PARTIALLY_PAID_WITH_A_LOSS',
            'PARTIALLY_PAID_WITH_A_CREDITED',
            'PARTIALLY_PAID_WITH_A_TERMINATED',
        ]

        const creditedStatuses = ['CREDITED']

        const terminatedStatuses = ['TERMINATED']

        // Could use switch-case but couldn't get this to work with .includes() method without haxs.
        if (outstandingStatuses.includes(this.status)) {
            return {
                text: t('portal.invoice.status.outstanding'),
                color: 'orange',
            }
        }

        if (partiallyPaidStatuses.includes(this.status)) {
            return {
                text: t('portal.invoice.status.partiallyPaid'),
                color: 'orange',
            }
        }

        if (paidStatuses.includes(this.status)) {
            return {
                text: t('portal.invoice.status.paid'),
                color: 'green',
            }
        }

        if (creditedStatuses.includes(this.status)) {
            return {
                text: t('portal.invoice.status.credited'),
                color: 'green',
            }
        }

        if (terminatedStatuses.includes(this.status)) {
            return {
                text: t('portal.invoice.status.terminated'),
                color: 'red',
            }
        }

        return {
            text: t('portal.invoice.status.unknown'),
            color: 'gray',
        }
    }
}
